import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Plus, Sparkles, Check } from 'lucide-react';
import { Product } from '../../data/mockProducts';
import { useCart } from '../../hooks/useCart';
interface RecipeBundleProps {
  title: string;
  description: string;
  products: Product[];
  totalPrice: number;
  savings?: number;
}
export function RecipeBundle({
  title,
  description,
  products,
  totalPrice,
  savings
}: RecipeBundleProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const {
    addToCart
  } = useCart();
  const handleAddBundle = (e: React.MouseEvent) => {
    e.stopPropagation();
    products.forEach(product => addToCart(product));
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };
  return <motion.div layout onClick={() => setIsExpanded(!isExpanded)} className="bg-gradient-to-br from-[#FFF8F6] to-white rounded-2xl shadow-sm border border-[#D97757]/20 overflow-hidden cursor-pointer relative" data-id="element-178">
      <div className="p-5" data-id="element-179">
        <div className="flex items-start justify-between mb-2" data-id="element-180">
          <div className="flex items-center gap-2" data-id="element-181">
            <div className="bg-[#D97757]/10 p-1.5 rounded-lg" data-id="element-182">
              <Sparkles className="w-5 h-5 text-[#D97757]" data-id="element-183" />
            </div>
            <span className="text-xs font-bold text-[#D97757] uppercase tracking-wider" data-id="element-184">
              Smart Bundle
            </span>
          </div>
          {savings && <motion.span initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="bg-[#2D5F3F] text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-185">
              Save ${savings.toFixed(2)}
            </motion.span>}
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-186">{title}</h3>
        <p className="text-gray-600 text-sm mb-4" data-id="element-187">{description}</p>

        <div className="flex items-center justify-between" data-id="element-188">
          <div className="flex -space-x-3" data-id="element-189">
            {products.slice(0, 4).map((p, i) => <motion.div key={p.id} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1
          }} className="w-10 h-10 rounded-full border-2 border-white bg-gray-100 overflow-hidden relative z-10" style={{
            zIndex: 10 - i
          }} data-id="element-190">
                <img src={p.image} alt="" className="w-full h-full object-cover" data-id="element-191" />
              </motion.div>)}
            {products.length > 4 && <div className="w-10 h-10 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 relative z-0" data-id="element-192">
                +{products.length - 4}
              </div>}
          </div>
          <div className="text-right" data-id="element-193">
            <span className="block text-xs text-gray-500" data-id="element-194">
              Total Bundle Price
            </span>
            <span className="text-lg font-bold text-[#2D5F3F]" data-id="element-195">
              ${totalPrice.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-196">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="border-t border-[#D97757]/10 bg-white/50 overflow-hidden" data-id="element-197">
            <div className="p-5 pt-2" data-id="element-198">
              <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-199">
                Included Items:
              </h4>
              <ul className="space-y-3 mb-6" data-id="element-200">
                {products.map((product, index) => <motion.li key={product.id} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} className="flex items-center justify-between text-sm" data-id="element-201">
                    <div className="flex items-center gap-3" data-id="element-202">
                      <img src={product.image} alt="" className="w-8 h-8 rounded-md object-cover bg-gray-100" data-id="element-203" />
                      <span className="text-gray-700" data-id="element-204">{product.name}</span>
                    </div>
                    <span className="font-medium text-gray-900" data-id="element-205">
                      ${product.price.toFixed(2)}
                    </span>
                  </motion.li>)}
              </ul>

              <motion.button whileTap={{
            scale: 0.98
          }} onClick={handleAddBundle} className={`w-full font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors ${isAdded ? 'bg-[#2D5F3F] text-white' : 'bg-[#D97757] text-white hover:bg-[#c66545]'}`} data-id="element-206">
                <AnimatePresence mode="wait" data-id="element-207">
                  {isAdded ? <motion.div key="added" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex items-center gap-2" data-id="element-208">
                      <Check className="w-5 h-5" data-id="element-209" />
                      Added to Cart!
                    </motion.div> : <motion.div key="add" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex items-center gap-2" data-id="element-210">
                      <Plus className="w-5 h-5" data-id="element-211" />
                      Add All to Cart
                    </motion.div>}
                </AnimatePresence>
              </motion.button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="absolute top-5 right-5 text-gray-400" data-id="element-212">
        <motion.div animate={{
        rotate: isExpanded ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-213">
          <ChevronDown className="w-5 h-5" data-id="element-214" />
        </motion.div>
      </div>
    </motion.div>;
}